import React, { useState, useEffect } from 'react';
import {
  NumberInput,
  BooleanInput,
  useResourceContext,
  useRecordContext,
  TabbedForm,
  FormTab,
} from 'react-admin';
import PositionInput from 'components/PositionInput';
import MoneyInput from 'components/MoneyInput';
import SpecialtyInput from 'components/SpecialtyInput';
import StateInput from 'components/StateInput';
import MarketInput from 'components/MarketInput';
import useCheckAccess from 'hooks/useCheckAccess';
import LocationInput from 'components/LocationInput';
import AgencyInput from 'components/AgencyInput';
import { pick } from 'ramda';
import DateInput from 'components/DateInput';
import { auth } from 'providers/utils';
import { Divider } from '@medely/web-components';
import { Alert, Box, FormControlLabel, Switch, Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import {
  Ten99Form,
  W2LocalForm,
  W2TravelForm,
} from '../rate-settings-form/W2And1099SubSettingsForm';
import LocationTypeInput from 'components/LocationTypeInput';
import useGate from 'hooks/useGate';
import { gateNames } from 'constants/statsig';
import {
  formatCents,
  getInherited,
  getRateSource,
} from 'utils/rateSettings/SharedRateSettingsForm.utils';

const EDITABLE_ACTIVE_RESOURCES = ['LocationPositionRateSetting', 'MarketPositionRateSetting'];

const ACTIVE_FIELDS = [
  {
    asset: 'per-diem shifts',
    field: 'can_post_job',
  },
  {
    asset: 'assignments',
    field: 'can_post_assignment',
  },
];

// TODO(FIN-2516): Delete hasLastRateReviewEdit once proper permissions is implemented.
const hasLastRateReviewEdit = () => {
  const APPROVED_EMAILS = [
    'angie@medely.com',
    'dblancett@medely.com',
    'kbogan@medely.com',
    'kera.quintanilla@medely.com',
  ];
  const currentUserEmail = auth.currentUser.email;

  return APPROVED_EMAILS.includes(currentUserEmail);
};

const getRateReferenceComponent = (source) => {
  if (source === 'market_location_type_id') {
    return (
      <>
        <MarketInput source="market_id" />
        <LocationTypeInput source="location_type_id" />
      </>
    );
  } else if (source === 'market_id') {
    return <MarketInput source="market_id" />;
  } else if (source === 'state_id') {
    return <StateInput source="state_id" />;
  } else if (source === 'location_type_id') {
    return <LocationTypeInput source="location_type_id" />;
  } else if (source === 'location_id') {
    return <LocationInput source="location_id" />;
  } else if (source === 'state_location_type_id') {
    return (
      <>
        <StateInput source="state_id" />
        <LocationTypeInput label="Location Type" source="location_type_id" />
      </>
    );
  } else if (source === 'agency_id') {
    return (
      <>
        <LocationInput source="location_id" />
        <AgencyInput source="agency_id" />
      </>
    );
  }
  return () => null;
};

const ActiveForPostingField = ({ asset, settingsField, field }) => {
  const label = `Active for ${asset}`;
  const { setValue } = useFormContext();
  const resource = useResourceContext();
  const record = useRecordContext();

  const source = `${settingsField}.${field}`;
  const recordValue = record[settingsField]?.[field];

  const [fieldValue, setFieldValue] = useState<boolean>(!!recordValue);
  const [isOverriding, setIsOverriding] = useState<boolean>(false);
  const isUnset = recordValue === null;

  const toggleOverride = () => setIsOverriding(!isOverriding);
  const showField = !isUnset || isOverriding;

  useEffect(() => {
    setValue(source, recordValue);
    setFieldValue(!!recordValue);
  }, [recordValue]);

  useEffect(() => {
    if (!showField) {
      setFieldValue(null);
      setValue(source, null);
    }
  }, [showField]);

  useEffect(() => {
    if (isOverriding) {
      setValue(source, false, { shouldDirty: true, shouldTouch: true });
    }
  }, [isOverriding]);

  const helperTextSuffix = fieldValue
    ? `allowed to post ${asset}`
    : `prevented from posting ${asset}`;
  const helperColor = fieldValue ? 'success.main' : 'error.main';

  return (
    <>
      {isUnset && (
        <>
          <FormControlLabel
            sx={{ width: '100%' }}
            label={`Override "${label}"*`}
            control={<Switch checked={isOverriding} onClick={toggleOverride} />}
          />
          <Typography variant="body2" color="error.main" pb="20px">
            {`* "${label}" is currently unset on this ${resource}, \
            and is being imported from parent rate setting.`}
            <br />
            {`You may change this configuration \
              by checking this "Override" toggle.`}
          </Typography>
        </>
      )}
      {showField && (
        <BooleanInput
          defaultValue={recordValue}
          label={label}
          source={source}
          onClick={() => setFieldValue(!fieldValue)}
          value={!!fieldValue}
          helperText={
            <Typography color={helperColor}>{`Locations will be ${helperTextSuffix}`}</Typography>
          }
        />
      )}
    </>
  );
};

const PositionRateSettingsForm = ({ settingsField = null }: { settingsField: string }) => {
  const record = useRecordContext();
  //  Custom creation from parent records uses temp IDs like 'market_position_rate_setting_id_123'
  const isCustomCreate = record?.id?.toString().includes('id');
  const isEdit = Boolean(record?.id);
  const resource = useResourceContext();
  const checkAccess = useCheckAccess();
  const source = getRateSource(record, resource);
  const RateReferenceComponent = getRateReferenceComponent(source);
  const { value: blockCreateRateSettings } = useGate(gateNames.blockCreateNewRateSettings);

  const inheritedSettings = record?.settings;

  const defaultValues =
    record &&
    pick(
      [
        record && 'id',
        'position_id',
        'state_id',
        'location_id',
        'specialty_id',
        'active',
        settingsField,
      ],
      record,
    );

  const showPostAndAgencyRates = isEdit && EDITABLE_ACTIVE_RESOURCES.includes(resource);
  const isLocationPositionRateSetting = resource === 'LocationPositionRateSetting';
  const isLocationTypePositionRateSetting =
    resource === 'MarketLocationTypePositionRateSetting' ||
    resource === 'StateLocationTypePositionRateSetting';

  const getActiveSource = () => {
    if (resource === 'MarketLocationTypePositionRateSetting') {
      return 'market_location_type_position_rate_setting_active';
    }
    if (resource === 'StateLocationTypePositionRateSetting') {
      return 'state_location_type_position_rate_setting_active';
    }
    return 'active';
  };

  const canUpdateSettings = checkAccess('update', resource, 'settings');
  const locationTaxPlan = record?.location?.primary_billing_group?.plan;
  const showTen99Settings =
    canUpdateSettings &&
    (!isLocationPositionRateSetting || (!!locationTaxPlan ? locationTaxPlan === 'standard' : true));
  const showW2Settings =
    canUpdateSettings &&
    (!isLocationPositionRateSetting ||
      (!!locationTaxPlan ? locationTaxPlan === 'enterprise_w2' : true));

  if (blockCreateRateSettings) {
    return (
      <Alert severity="warning">
        You cannot create or edit rate settings today due to a Fintech team deployment. If you have
        any questions, please reach out using the software_support Slack channel.
      </Alert>
    );
  }

  return (
    <TabbedForm defaultValues={defaultValues}>
      <FormTab label="General">
        {RateReferenceComponent}
        <PositionInput />
        <SpecialtyInput source="specialty_id" positionSource="position_id" optionText="label" />
        <BooleanInput label="Active" source={getActiveSource()} defaultValue={true} />
        {showPostAndAgencyRates && (
          <Box width="100%">
            <Divider boxProps={{ sx: { paddingY: '20px' } }} />
            {ACTIVE_FIELDS.map(({ asset, field }) => (
              <>
                <ActiveForPostingField
                  key={`active-field-for-${asset}`}
                  asset={asset}
                  settingsField={settingsField}
                  field={field}
                />
                <Divider boxProps={{ sx: { paddingY: '20px' } }} />
              </>
            ))}
          </Box>
        )}
        {resource === 'MarketPositionRateSetting' && hasLastRateReviewEdit() && (
          <DateInput label="Last Review Date" source="last_rate_review" />
        )}
        <MoneyInput
          label="Location On Call Rate"
          source={`${settingsField}.charge_on_call_hourly_rate_cents`}
          helperText={formatCents(inheritedSettings?.charge_on_call_hourly_rate_cents)}
        />
        <NumberInput
          label="On Call Percentage Fee"
          source={`${settingsField}.on_call_fee_percent`}
          helperText={getInherited(inheritedSettings, 'on_call_fee_percent')}
        />
        {isLocationTypePositionRateSetting && (
          <NumberInput
            label="Location Type Markup"
            source={`${settingsField}.location_type_markup`}
            helperText={`${(
              Number(getInherited(inheritedSettings, 'location_type_markup') || 0) * 100
            ).toFixed(2)}`}
            format={(v) => v && Number((v * 100).toFixed(2))}
            parse={(v) => v && parseFloat(v) / 100}
          />
        )}
        {showPostAndAgencyRates && (
          <MoneyInput
            label="Agency Charge Rate"
            source={`${settingsField}.agency_charge_rate_cents`}
            helperText={formatCents(inheritedSettings?.agency_charge_rate_cents)}
          />
        )}
      </FormTab>
      <FormTab label="1099" disabled={!showTen99Settings}>
        <Ten99Form source={settingsField} inheritedSettings={inheritedSettings} showTitle={false} />
      </FormTab>
      <FormTab label="W2 LOCAL" disabled={!showW2Settings}>
        <W2LocalForm
          source={settingsField}
          inheritedSettings={inheritedSettings}
          showTitle={false}
        />
      </FormTab>
      <FormTab label="W2 TRAVEL" disabled={!canUpdateSettings}>
        <W2TravelForm
          source={settingsField}
          inheritedSettings={inheritedSettings}
          showTitle={false}
        />
      </FormTab>
    </TabbedForm>
  );
};

export default PositionRateSettingsForm;
